<template>
    <div class="row prescription-row" v-show="!hashPath || hashPath === 'prescription'">
        <div class="col-12 col-sm-3 col-md-5 col-lg-5">
            <div class="row note-list">
                <div v-for="(note, index) in leftSideNotes" :key="index" class="col-12 col-md-12 note-section">
                    <SpecialNote
                        v-if="specialNoteTypes.includes(note.value)"
                        :note="note"
                        :index="index"
                        @openModal="openAddNoteModal"
                        :medicineNotes="medicineNotes"
                    />
                    <GeneralNoteDentition
                      v-else-if="dentitionNoteType.includes(note.value)"
                      :note="note"
                      :index="index"
                      :dentition="dentition"
                      v-model:dentitionType="dentitionType"
                      @openModal="openAddNoteModal"
                    />
                    <CcNote
                      v-else-if="note.value === 'chief_complaint'"
                      :note="note"
                      :index="index"
                      @openModal="openAddNoteModal"
                      ref="ccNoteRef"
                    />
                    <GeneralNote
                        v-else
                        :note="note" 
                        :index="index"
                        @openModal="openAddNoteModal"
                    />
                </div>
            </div>
        </div>
        <div class="col-12 col-sm-8 col-md-7 col-lg-7 medicine-seg">
            <div class="row rx-notes">
                <div v-for="(note, index) in rightSideNotes" :key="index" class="col-12 col-md-12 note-section">
                    <SpecialNote
                        v-if="specialNoteTypes.includes(note.value)"
                        :note="note"
                        :index="index"
                        @openModal="openAddNoteModal"
                        :medicineNotes="medicineNotes"
                    />
                    <GeneralNote
                        v-else
                        :note="note" 
                        :index="index"
                        @openModal="openAddNoteModal"
                    />
                </div>
            </div>
        </div>

        <div class="col-12 footer-section">
            <SubmitCancelBtn
                @onSubmit="submit(false, 'prescription', false)"
                @onCancel="onClickToClose"
                :loading="loading"
                submitTxt="Save Only"
            >
                    <template #additional-buttons-middle>
                        <button
                            @click="submit(true, 'prescription', false)"
                            :disabled="loading"
                            class="btn btn-success me-1 mt-1 waves-effect waves-float waves-light"
                        >
                            <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            Save and Print
                        </button>
                        <button
                            @click="submit(false, 'prescription', true)"
                            :disabled="loading"
                            class="btn btn-secondary me-1 mt-1 waves-effect waves-float waves-light"
                        >
                            <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            Save and Billing
                        </button>
                    </template>
            </SubmitCancelBtn>
        </div>
        
        <AddGeneralNoteModal
            v-if="store.state.isModalOpen"
            :note="singleNote"
        />

        <AddGeneralNoteDentitionModal
            v-if="store.state.isModalOpenThree"
            :note="singleNote"
            :modalState="'isModalOpenThree'"
        />

        <AddSpecialNoteModal
            v-if="store.state.isModalOpenTwo"
            :note="singleNote"
        />
        <Loader v-if="loader"/>
    </div>
    <div>
        <Teleport v-if="canTeleport && service" to="#tooth-numbering">
            <MiddleHeader 
                :resourceDescriptions="resourceDescriptions"
                :treatmentPlan="treatmentPlan"
                :ipdRegister="service.ipd_register"
                :bill="service"
                :generalId="generalId"
                :defaultProductId="defaultProductId"
                :dentition="dentition"
                v-model:dentitionType="dentitionType"
                v-show="(hashPath === 'treatment_plan')"
            /> 
        </Teleport>
    </div>
    <div>
        <Teleport v-if="canTeleport && service" to="#dentition-type">
            <div v-for="item in (dentition ? dentition.types : [])" :key="item.value" class="form-check form-check-inline mt-0" :class="generalId ? 'disabled-element' : ''">
                <input 
                    v-model="dentitionType" 
                    class="form-check-input" type="radio" 
                    name="inlineRadioOptions"
                    :id="`inlineRadio${item.value}`"
                    :value="item.value"
                />                                
                <label class="form-check-label" :for="`inlineRadio${item.value}`">{{ item.name }}</label>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import {inject, onMounted, ref, watch} from 'vue';
import GeneralNote from '@/components/molecule/company/dental-os/service/note/GeneralNote.vue';
import GeneralNoteDentition from '@/components/molecule/company/dental-os/service/note/GeneralNoteDentition.vue';
import SpecialNote from '@/components/molecule/company/dental-os/service/note/SpecialNote.vue';
import AddGeneralNoteModal from '@/components/molecule/company/dental-os/service/note/AddGeneralNoteModal.vue';
import AddGeneralNoteDentitionModal from '@/components/molecule/company/dental-os/service/note/AddGeneralNoteDentitionModal.vue';
import AddSpecialNoteModal from '@/components/molecule/company/dental-os/service/note/AddSpecialNoteModal.vue';
import CcNote from '@/components/molecule/company/dental-os/service/note/CcNote.vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from "@/components/atom/LoaderComponent";
import handleDentalOs from '@/services/modules/dental-os/dentalOs';
import SubmitCancelBtn from '@/components/atom/SubmitCancelBtn.vue';
import MiddleHeader from '@/components/molecule/company/dental-os/service/middle-header/MiddleHeader.vue';
import { computed } from 'vue';
import handleCompany from "@/services/modules/company";
import writeDentalPrescriptionHelper from '@/services/utils/pdf/writeDentalPrescriptionHelper';
import handlePurchase from "@/services/modules/purchase";

const props = defineProps({
    data: {
        type: Object || null,
        required: false
    },
    currentTab: {
        type: String,
        required: true
    }
});

const emit = defineEmits(['reset']);

const { fetchSingleDoctorOrder, storeDentalPrescription, updateDentalPrescription } = handleDentalOs();
const { fetchCompanyInfo } = handleCompany();
const { generatePdf } = writeDentalPrescriptionHelper();
const { fetchSalesOrderById } = handlePurchase();

const service = ref(null);
const dentition = ref(null);
const store = useStore();
const singleNote = ref(null);
const route = useRoute();
const router = useRouter();
const loader = ref(false);
const loading = ref(false);
const specialNoteTypes = ref(['investigation', 'rx']);
const dentitionNoteType = ref(['diagnosis', 'on_examinations', 'dental_history'])
const rightSideNotes = ref([]);	
const leftSideNotes = ref([])
const medicineNotes = ref([])
const canTeleport = ref(false)
const dentitionType = ref('permanent');
const updatedService = ref(null);
const ccNoteRef = ref(null);

const companyInfo = ref(null);
const latestOrder = ref(null);

const showError = inject('showError');
const showSuccess = inject('showSuccess');
const resourceDescriptions = computed(() => {
    return service.value && service.value.service_resource ? service.value.service_resource.resource_descriptions : []
});

const defaultProductId = computed(() => resourceDescriptions.value.length > 0 ? resourceDescriptions.value[0].product_id : null);
const generalId = computed(() => route.query.general_id ? parseInt(route.query.general_id) : null);
const salesOrderMasterId = computed(() => service.value.ipd_register ? service.value.ipd_register.sales_order_id : null);
const doctorOrderId = computed(() => service.value.doctor_order ? service.value.doctor_order.id : null);
const serviceStatus = computed(() => route.query.service_status ? route.query.service_status : null);
const salesOrderGeneralId = ref(null);
const salesOrder = ref(null);

const treatmentPlan = ref({
    name: 'Treatment Plan',
    selectedNotes: [],
    value: 'treatment_plan'
});

const openAddNoteModal = ({note, index}) => {
    singleNote.value = null;
    singleNote.value = {
        index: index,
        name: note.name,
        value: note.value,
        selectedNotes: note.selectedNotes,
        preAddedNotes: [],
        noteMasters: [],
        newNotesByDoctor: note.newNotesByDoctor
    }

    if(specialNoteTypes.value.includes(note.value)){
        store.state.isModalOpenTwo = true;
        return;
    }

    if(dentitionNoteType.value.includes(note.value)){
      store.state.isModalOpenThree = true;
      return;
    }
    
    store.state.isModalOpen = true;
}

const submit = async(shouldPrint = false, from = 'prescription', redirect = false) => {

    const formData = formateFormData();

    if (from === 'treatment_plan') {
        formData.save_treatment_only = 1;
    }

    const prescriptionAction = doctorOrderId.value ? updateDentalPrescription : storeDentalPrescription

    loading.value = true
    await prescriptionAction(formData, generalId.value).then(async (res) => {
        loading.value = false
        if (!res.status) return showError(res.message);

        if(res.data && res.data.sales_order_general_id) {
            salesOrderGeneralId.value = res.data.sales_order_general_id;
        }

        if (shouldPrint) {
            await goToPrint();
        }

        await new Promise(resolve => setTimeout(resolve, 500));

        await onClickRedirectSamePage(res.data, from, redirect);

        emit('reset');

        if (from !== 'treatment_plan') {
            return showSuccess(res.message);
        }

    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const onClickRedirectSamePage = (data, from = 'prescription', redirect = false) => {
    const url = {
        name: 'dental-service',
        params: {
            ...route.params
        },
        query: {
            ...route.query,
            general_id: data.sales_order_general_id,
            ipd_register_id: data.ipd_register_id,
            contact_id: service.value.contact_profile_id,
            order_master_id: data.sales_order_master_id
        },
        hash: '#prescription'
    }

    if (from === 'treatment_plan') {
        url.hash = '#treatment_plan';
    }

    if (redirect) {
        url.query.tab = 'ipd-bill';
        url.hash = '#billing';
    }
    
    return router.push(url);
}

const getQuery = () => {
    const salesGeneralId = salesOrderGeneralId.value || generalId.value;

    let query = `?company_id=${route.params.companyId}`;
    query += `&sales_order_general_id=${salesGeneralId}`;

    return query;
}

const goToPrint = async () => {
    let salesOrderId = null;
    const serviceId = route.params.serviceId;
    const query = getQuery();
    const isHideFooter = true;
    const isHideHeader = true;

    loader.value = true;

    try {
        const res = await fetchSingleDoctorOrder(query, serviceId);
        if (!res.status) return showError(res.message)
        updatedService.value = res.data.bill;
        salesOrderId = updatedService.value.ipd_register ? updatedService.value.ipd_register.sales_order_id : null;

        const salesOrderRes = await fetchSalesOrderById(salesOrderId, query);
        if (salesOrderRes.status) {
            salesOrder.value = salesOrderRes.data;
        }
        
    } catch (error) {
        console.log(error);
    }

    await generatePdf(
        companyInfo.value, 
        leftSideNotes.value, 
        rightSideNotes.value, 
        updatedService.value, 
        isHideHeader, 
        isHideFooter,
        salesOrder.value,
    );

    loader.value = false;
}

const onClickToClose = () => {
    const backUrl = route.query.back_url ? decodeURIComponent(route.query.back_url) : null;
    if (backUrl) {
        router.push(backUrl);
        return;
    }

    let routeName = 'my-dental-schedule';

    if (route.query.from === 'list') {
        routeName = 'my-dental-schedule-list';
    }

    router.push({
        name: routeName,
        params: route.params,
        query: {
            start: route.query.start,
            end: route.query.end,
            page: route.query.page || 1
        }
    })
}

const formateFormData = () => {
    const {rx, investigation, other_notes} = formatNotes();

    const formData = {
        rx_notes: rx,
        investigations: investigation,
        other_notes: other_notes,
        contact_profile_id: service.value.contact_profile_id,
        service_resource_id: service.value.service_resource_id,
        service_general_id: service.value.id,
        company_id: route.params.companyId,
        sales_order_master_id: salesOrderMasterId.value,
        sales_order_general_id: generalId.value,
        default_product_id: defaultProductId.value,
        dentition_type: dentitionType.value,
        ipd_register_id: service.value.ipd_register ? service.value.ipd_register.id : null,
        doctor_order_id: doctorOrderId.value,
        prescription_type: 'dental_os',
        item_details: formatItemDetails()
    }

    if (route.query.task_id) {
        formData.task_id = route.query.task_id;
    }
    
    return formData;
}

const formatItemDetails = () => {
    const itemDetails = treatmentPlan.value.selectedNotes.map(item => {
        item.contact_profile_id = item.service_resource ? item.service_resource.id : null;
        item.sales_order_master_id = salesOrderMasterId.value;
        return item;
    })
    return itemDetails;
}

const formatNotes = () => {
    const noteObj = {
        rx: [],
        investigation: [],
        other_notes: [],
    };

    const allNotes = [...leftSideNotes.value, ...rightSideNotes.value].filter(item => item.selectedNotes.length);
    
    for(let i = 0; i < allNotes.length; i++){

        const note = allNotes[i];

        if(noteObj.hasOwnProperty(note.value)){
            const formattedSelectedNotes = note.selectedNotes.map(item => {
                item.group_index = i;
                item.order_date = note.date || '';
                item.consultant_id = note.service_resource ? note.service_resource.id : null;
                return item;
            })

            if(note.newNotesByDoctor.length){
                noteObj.other_notes = noteObj.other_notes.concat(setMedicineData(note.newNotesByDoctor));
            }

            noteObj[note.value] = noteObj[note.value].concat(formattedSelectedNotes);

            continue;
        }

        noteObj['other_notes'].push({
            id: note.id ? note.id : null,
            type : note.value,
            note : JSON.stringify(note.selectedNotes),
            new_notes: note.newNotesByDoctor,
            marked_positions: note.markedPositions ? JSON.stringify(note.markedPositions) : null
        });
    }

    return noteObj;
} 

const setMedicineData = (notes) => {
    return notes.map(note => {
        const newNote = [ note.name ];
        return {
            id: note.id,
            type : note.type,
            note : JSON.stringify(newNote),
            new_notes: newNote
        }
    })
}

const setPrescriptionData = (data) => {
    service.value = data.bill;
    dentition.value = data.dentition;
    latestOrder.value = data.latest_prescription ? data.latest_prescription.doctor_order : null;
    leftSideNotes.value = data.prescription_details.left_side_notes
    rightSideNotes.value = data.prescription_details.right_side_notes
    medicineNotes.value = data.prescription_details.medicine_notes
    dentitionType.value = service.value.ipd_register && service.value.ipd_register.dentition_type ? service.value.ipd_register.dentition_type : 'permanent';
    setDoctorOrderData();
}

const setDoctorOrderData = () => {

    const doctorOrder = service.value.doctor_order || latestOrder.value;

    if(! doctorOrder) return;

    if(doctorOrder.investigation_orders.length){
        setInvestigationOrderData(doctorOrder.investigation_orders, 'investigation');
    }

    if(doctorOrder.medicine_orders.length){
        setMedicineOrderData(doctorOrder.medicine_orders);
    }

    if(doctorOrder.notes.length){
        setNoteData(doctorOrder.notes);
    }
}

const setMedicineOrderData = (medicineRx) => {
    
    let rxNote = rightSideNotes.value.find(item => item.value === 'rx');
    if(!rxNote) return;
    
    let formattedMedicineRx = formatMedicineRx(medicineRx);
    rxNote.selectedNotes = formattedMedicineRx;
}

const formatMedicineRx = (medicineRx) => {
    return medicineRx.map((medicine) => {
        let item = {
            id: medicine.id,
            product_id: medicine.product_id,
            service_resource_id: medicine.service_resource,
            name: medicine.product.name,
            remarks: medicine.remarks,
            isSelected: true,
            isEdited: true,
            dose: medicine.dose,
            unit: medicine.unit,
            duration: medicine.duration,
            madication_status: medicine.madication_status,
            is_published: medicine.is_published,
            group_index: medicine.group_index,
            order_date: medicine.order_date,
            created_at: medicine.created_at,
            status: medicine.status
        };

        if(latestOrder.value && item.status === 'inactive'){
            item.is_published = false;
        }

        return item;
    })
}

const setInvestigationOrderData = (medicineRx, noteType) => {

    let prevNote = leftSideNotes.value.find(item => item.value === noteType);
    if(!prevNote) return;

    for(let note of medicineRx){

        const noteObj = {
            id: note.id,
            product_id: note.product_id,
            name: note.product.name,
            remarks: note.remarks,
            isSelected: true,
            status: 'active',
            is_published: note.is_published,
        };

        prevNote.selectedNotes.push(noteObj)
    }
}

const setNoteData = (notes) => {
    const leftSideNoteKeys = leftSideNotes.value.reduce((acc, item, index) => {
                    acc[item.value] = index;
                    return acc;
                }, {});

    const rightSideNoteKeys = rightSideNotes.value.reduce((acc, item, index) => {
                acc[item.value] = index;
                return acc;
            }, {});

    notes.forEach((noteItem) => {
        
        const noteIndex = rightSideNoteKeys[noteItem.type];

        if(noteIndex !== undefined){
            rightSideNotes.value[noteIndex].id = noteItem.id;
            rightSideNotes.value[noteIndex].selectedNotes = noteItem.note;
            rightSideNotes.value[noteIndex].markedPositions = noteItem.marked_positions;
            return;
        }

        const leftSideNoteIndex = leftSideNoteKeys[noteItem.type];
        
        if(leftSideNoteIndex !== undefined){
            leftSideNotes.value[leftSideNoteIndex].id = noteItem.id;
            leftSideNotes.value[leftSideNoteIndex].selectedNotes = noteItem.note;
            leftSideNotes.value[leftSideNoteIndex].markedPositions = noteItem.marked_positions;
        }
    });
}

const getCompanyInfo = async () => {
    await fetchCompanyInfo(route.params.companyId).then((res) => {
        companyInfo.value = res.data
    })
}

const hashPath = computed(() => {
    return route.hash.substring(1)
});

watch(() => ccNoteRef.value, (newVal) => {
    const refVal = newVal && newVal.length ? newVal[0] : null;
    store.commit('dental/setCcNoteRef', refVal);
}, {deep: true})

onMounted(async () => {
    if(props.data) {
        setPrescriptionData(props.data);
    }
    
    canTeleport.value = !!document.getElementById('tooth-numbering')

    getCompanyInfo();

    store.commit('dental/setCcNoteRef', ccNoteRef.value);
})

defineExpose({
    submit
})
</script>

<style scoped>
.prescription-row {
    min-height: 900px;
}
.note-section {
    margin-top: 2%;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    width: 96%;
    margin-left: 2%;
}

.footer-section{
    text-align: center;
    padding: 3% 0;
}

.treatment-footer {
    text-align: center;
    min-height: 65px;
}

.note-list{
    margin-left: 0%;
    margin-top: 2%;
}
.medicine-seg{
    border-left: 1px solid black;
    min-height: 800px;
}
.rx-notes{
    padding-right: 1rem;
}
</style>